"use client"

import React, { useState, useMemo } from "react"
import cx from "classnames"
import {
  AZURO_MARKET_GROUPS,
  isMarketInGroup,
} from "src/constants/azuroMarketGroups"
import type { GameMarkets } from "@azuro-org/toolkit"

const ALL_MARKET_TABS = [
  { id: "full_time_result", label: "Full Time Result" },
  { id: "handicap", label: "Handicap" },
  { id: "totals", label: "Totals" },
  { id: "1st_half", label: "1st Half Result" },
  { id: "2nd_half", label: "2nd Half Result" },
  { id: "quarters", label: "Quarters" },
  { id: "periods", label: "Periods" },
  { id: "both_teams_score", label: "Both Teams Score" },
  { id: "odd_even", label: "Odd/Even" },
  { id: "team_specific", label: "Team Specific" },
  { id: "maps", label: "Maps" },
  { id: "sets", label: "Sets" },
  { id: "special", label: "Special" },
]

type MarketTabsProps = {
  activeTab?: string
  onTabChange?: (tabId: string) => void
  className?: string
  availableMarkets?: GameMarkets
}

const MarketTabs: React.FC<MarketTabsProps> = ({
  activeTab = "",
  onTabChange,
  className,
  availableMarkets = [],
}) => {
  const [selectedTab, setSelectedTab] = useState(activeTab)

  // Filter tabs to show only those that have available markets
  const availableTabs = useMemo(() => {
    if (!availableMarkets.length) {
      return ALL_MARKET_TABS
    }

    return ALL_MARKET_TABS.filter(tab => {
      // Check if any market in the current game belongs to this tab's group
      return availableMarkets.some(market => {
        return isMarketInGroup(market.marketKey, tab.id)
      })
    })
  }, [availableMarkets])

  const handleTabClick = (tabId: string) => {
    // Allow deselecting the currently active tab
    if (selectedTab === tabId) {
      setSelectedTab("")
      onTabChange?.("")
    } else {
      setSelectedTab(tabId)
      onTabChange?.(tabId)
    }
  }

  // If no tabs are available, don't render anything
  if (availableTabs.length === 0) {
    return null
  }

  return (
    <div
      className={cx(
        "flex items-center gap-1 p-1 bg-accent-grey-900 rounded-lg overflow-x-auto",
        className
      )}
    >
      {availableTabs.map(tab => {
        const isActive = selectedTab === tab.id

        return (
          <button
            key={tab.id}
            onClick={() => handleTabClick(tab.id)}
            className={cx(
              "px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap",
              {
                "bg-accent-grey-700 text-white": isActive,
                "text-accent-grey-300 hover:text-white hover:bg-accent-grey-800":
                  !isActive,
              }
            )}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

export default MarketTabs
